import { WarningCircleIcon } from '@phosphor-icons/react';
import { type SubmitEvent, useRef } from 'react';

import { useContent } from '@/content/ContentContext';
import { BriefSuccess } from '@/features/brief/BriefSuccess';
import { ContactStep } from '@/features/brief/steps/ContactStep';
import { ProjectStep } from '@/features/brief/steps/ProjectStep';
import { ScopeStep } from '@/features/brief/steps/ScopeStep';
import { useBriefCosmos } from '@/features/brief/useBriefCosmos';
import { useBriefWizard } from '@/features/brief/useBriefWizard';
import { Button } from '@/ui/Button/Button';

import styles from './Brief.module.css';

const STEP_VIEWS = { project: ProjectStep, scope: ScopeStep, contact: ContactStep } as const;

/** Le brief en trois étapes, avec progression, validation et confirmation. */
export function BriefForm() {
  const { brief } = useContent();
  const formRef = useRef<HTMLFormElement>(null);
  const controller = useBriefWizard(formRef);
  const { wizard } = controller;
  useBriefCosmos(wizard);

  if (wizard.status === 'sent' && wizard.receipt) {
    return (
      <BriefSuccess
        receipt={wizard.receipt}
        fullName={wizard.values.fullName}
        email={wizard.values.email}
        onRestart={controller.restart}
      />
    );
  }

  const handleSubmit = (event: SubmitEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (wizard.isLastStep) void controller.submit();
    else controller.next();
  };

  const StepView = STEP_VIEWS[wizard.step];
  const submitting = wizard.status === 'submitting';

  return (
    <form ref={formRef} noValidate onSubmit={handleSubmit} aria-labelledby="brief-progress">
      <div className={styles.progressHeader}>
        <p id="brief-progress">
          <span className={styles.stepCount}>
            {brief.stepLabel(wizard.stepNumber, wizard.totalSteps)}
          </span>
          {', '}
          {brief.steps[wizard.step].title}
        </p>
        <p className={styles.duration}>{brief.duration}</p>
      </div>
      <div className={styles.segments} aria-hidden="true">
        {Array.from({ length: wizard.totalSteps }, (_, index) => (
          <span
            key={index}
            className={styles.segment}
            data-done={index < wizard.stepNumber || undefined}
          />
        ))}
      </div>

      <StepView controller={controller} />

      {wizard.status === 'failed' && (
        <p className={styles.failure} role="alert">
          <WarningCircleIcon size={20} aria-hidden="true" />
          {brief.errors.unavailable}
        </p>
      )}

      <div className={styles.actions}>
        {wizard.stepNumber > 1 && (
          <Button variant="quiet" icon="none" onClick={controller.back} disabled={submitting}>
            {brief.actions.back}
          </Button>
        )}
        {wizard.isLastStep ? (
          <Button type="submit" icon="arrow-up-right" loading={submitting}>
            {submitting
              ? brief.actions.submitting
              : wizard.status === 'failed'
                ? brief.actions.retry
                : brief.actions.submit}
          </Button>
        ) : (
          <Button type="submit" icon="arrow-right">
            {brief.actions.next}
          </Button>
        )}
      </div>
    </form>
  );
}
